import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { isString } from 'lodash';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';
import { getComponentFromName } from '../../lib/utils';

import { useTheme } from '../../contexts/ThemeContext';
import { useThemeClassName } from '../../contexts/ThemeContext';
import Button from '../buttons/Button';
import * as IconComponents from '../icons/index';

import styles from '../../../styles/partials/prize-card.module.scss';

const propTypes = {
    image: AppPropTypes.image,
    name: PropTypes.string,
    description: PropTypes.string,
    quantity: PropTypes.number,
    note: PropTypes.string,
    status: AppPropTypes.message,
    badge: PropTypes.string,
    href: PropTypes.string,
    winnable: PropTypes.bool,
    className: PropTypes.string,
    imageClassName: PropTypes.string,
};

const defaultProps = {
    image: null,
    name: null,
    description: null,
    quantity: null,
    note: null,
    status: null,
    badge: null,
    href: null,
    winnable: true,
    className: null,
    imageClassName: null,
};

function PrizeCard({
    image,
    name,
    description,
    quantity,
    note,
    status,
    badge,
    href,
    winnable,
    className,
    imageClassName,
}) {
    const { name: themeName = null } = useTheme();
    const themeClassName = useThemeClassName(styles);
    const { url: imageUrl = null } = image || {};
    const hasHref = href !== null;
    const hasStatus = status !== null;
    const BadgeComponent = badge !== null ? getComponentFromName(IconComponents, badge) : null;

    const innerContent = useMemo(
        () => (
            <span className={styles.inner}>
                {imageUrl !== null ? (
                    <span className={classNames([styles.imageContainer, imageClassName])}>
                        <img className={styles.image} src={imageUrl} alt={name} />
                        {BadgeComponent !== null ? (
                            <BadgeComponent className={styles.badge} />
                        ) : null}
                    </span>
                ) : null}
                <span className={styles.content}>
                    {status !== null ? <span className={styles.status}>{status}</span> : null}
                    {name !== null ? <span className={styles.name}>{name}</span> : null}
                    {description !== null && isString(description) ? (
                        <span
                            className={styles.description}
                            dangerouslySetInnerHTML={{ __html: description }}
                        />
                    ) : null}
                    {description !== null && !isString(description) ? (
                        <span className={styles.description}>{description}</span>
                    ) : null}
                    {winnable && quantity !== null && quantity > 0 && themeName !== 'iga_noel' ? (
                        <span className={styles.quantity}>
                            <FormattedMessage
                                defaultMessage="{count, plural, =0 {# total available} one {# total available} other {# total available}}"
                                description="Prize quantity"
                                values={{ count: quantity }}
                            />
                        </span>
                    ) : null}
                    {note !== null ? <span className={styles.note}>{note}</span> : null}
                </span>
                {hasHref ? (
                    <span className={styles.arrow}>
                        <FontAwesomeIcon icon={faChevronRight} />
                    </span>
                ) : null}
            </span>
        ),
        [imageUrl, name, description, status, hasHref],
    );

    return (
        <div
            className={classNames([
                styles.container,
                'card-prize',
                {
                    [styles.disabled]: !winnable,
                    [styles.hasStatus]: hasStatus,
                },
                themeClassName,
                className,
            ])}
        >
            {href !== null ? (
                <Button className={styles.button} href={href}>
                    {innerContent}
                </Button>
            ) : (
                innerContent
            )}
        </div>
    );
}

PrizeCard.propTypes = propTypes;
PrizeCard.defaultProps = defaultProps;

export default PrizeCard;
